import React, { useEffect, useState } from "react";
import { WS_NOTIF_URL } from "../config";
import CallInteractionModal from "./CallInteractionModal";
import { useAuth } from "../Contexts/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell, faTimes, faCalendarCheck } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";

export default function AppNotificationHandler() {
  const { user } = useAuth();
  const [activeCall, setActiveCall] = useState(null);
  const [activeReminder, setActiveReminder] = useState(null);
  const [celebration, setCelebration] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const savedCall = localStorage.getItem("crm:pending_call");
    if (savedCall) {
      try {
        setActiveCall(JSON.parse(savedCall));
      } catch (e) {
        localStorage.removeItem("crm:pending_call");
      }
    }
  }, []);

  useEffect(() => {
    if (activeReminder) {
      const timer = setTimeout(() => {
        setActiveReminder(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [activeReminder]);

  useEffect(() => {
    if (activeCall) {
      localStorage.setItem("crm:pending_call", JSON.stringify(activeCall));
    } else {
      localStorage.removeItem("crm:pending_call");
    }
  }, [activeCall]);

  useEffect(() => {
    if (!user) return;

    const token = localStorage.getItem("accessToken");
    const socketUrl = `${WS_NOTIF_URL}?token=${token}`;
    
    let socket = null;
    let reconnectTimer = null;
    let reconnectDelay = 2000;
    let intentionallyClosed = false;

    const handleMessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log("WebSocket message received:", data);
        
        if (data.type === 'notification') {
          const notif = data.notification;
          
          if (notif.notification_type === 'call_received' || (notif.type === 'call_received')) {
            const callInfo = notif.data || notif;
            setActiveCall(callInfo);
          } 
          else if (notif.notification_type === 'event_reminder') {
            setActiveReminder(notif);
          }
        }
      } catch (err) {
        console.error("Failed to parse socket message", err);
      }
    };

    const connect = () => {
      if (intentionallyClosed) return;
      socket = new WebSocket(socketUrl);

      socket.onmessage = handleMessage;
      socket.onopen = () => {
        console.log("Notification WebSocket connected");
        reconnectDelay = 2000;
      };
      socket.onerror = (err) => console.error("Notification WebSocket error", err);
      socket.onclose = () => {
        if (intentionallyClosed) return;
        console.warn(`Notification WebSocket disconnected. Reconnecting in ${reconnectDelay / 1000}s…`);
        reconnectTimer = setTimeout(() => {
          reconnectDelay = Math.min(reconnectDelay * 2, 30000);
          connect();
        }, reconnectDelay);
      };
    };

    connect();

    return () => {
      intentionallyClosed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (socket) socket.close();
    };
  }, [user]);

  useEffect(() => {
    if (!user) return;
    
    // Add a small delay to ensure user profile data is fully loaded from AuthContext
    const timer = setTimeout(() => {
      const today = new Date();
      const tDay = today.getDate();
      const tMonth = today.getMonth(); // 0-11
      
      const todayKey = `${today.getFullYear()}-${tMonth + 1}-${tDay}`;
      const lastWishKey = `celebration_seen_v5_${user.id}_${todayKey}`;
      
      if (localStorage.getItem(lastWishKey)) return;

      console.log(`[Celebration Check] User: ${user.username}, BDay: ${user.date_of_birth}, Joined: ${user.date_joined_company}`);

      let isBirthday = false;
      let isAnniversary = false;
      let tenure = 0;

      // Check Birthday
      if (user.date_of_birth) {
        const [by, bm, bd] = user.date_of_birth.split('-').map(Number);
        if (bd === tDay && (bm - 1) === tMonth) isBirthday = true;
      }

      // Check Anniversary
      if (user.date_joined_company) {
        const [ay, am, ad] = user.date_joined_company.split('-').map(Number);
        if (ad === tDay && (am - 1) === tMonth) {
          isAnniversary = true;
          tenure = today.getFullYear() - ay;
        }
      }

      if (isBirthday && isAnniversary) {
        setCelebration({ 
          type: 'double', 
          title: `A Double Celebration! 🎂🎊`,
          subtitle: `Happy Birthday AND Happy ${tenure > 0 ? tenure + '-Year' : ''} Work Anniversary, ${user.first_name || user.username}! It's a truly special day!`,
          icon: "🌟"
        });
        localStorage.setItem(lastWishKey, 'true');
      } else if (isBirthday) {
        setCelebration({ 
          type: 'birthday', 
          title: `Happy Birthday, ${user.first_name || user.username}! 🎂`,
          subtitle: "Wishing you a fantastic day filled with joy and success!",
          icon: "🎂"
        });
        localStorage.setItem(lastWishKey, 'true');
      } else if (isAnniversary && tenure >= 0) {
        setCelebration({ 
          type: 'anniversary', 
          title: tenure === 0 ? "Welcome to the Team! 🎊" : "Happy Work Anniversary! 🎊", 
          subtitle: tenure === 0 
            ? "We are so excited to have you with us on your first day!" 
            : `Celebrating ${tenure} wonderful ${tenure === 1 ? 'year' : 'years'} with the team!`,
          icon: "🎊"
        });
        localStorage.setItem(lastWishKey, 'true');
      }
    }, 1500);

    return () => clearTimeout(timer);
  }, [user]);

  return (
    <>
      {/* 1. Telephony Interaction Modal */}
      {activeCall && (
        <CallInteractionModal 
          callData={activeCall} 
          onClose={() => setActiveCall(null)} 
        />
      )}

      {/* 2. Follow-up Reminder Toast */}
      {activeReminder && (
        <div className="notification-toast-overlay" style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            zIndex: 9999,
            animation: 'slideInRight 0.4s ease-out'
        }}>
            <div style={{
                background: '#fff',
                width: '320px',
                borderRadius: '12px',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                border: '1px solid #e2e8f0',
                padding: '16px',
                display: 'flex',
                gap: '12px',
                position: 'relative',
                overflow: 'hidden'
            }}>
                {/* Progress bar for auto-hide */}
                <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    height: '3px',
                    background: '#3b82f6',
                    width: '100%',
                    animation: 'progressShrink 5s linear forwards'
                }} />

                <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    background: '#eff6ff',
                    color: '#1d4ed8',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '18px',
                    flexShrink: 0
                }}>
                    <FontAwesomeIcon icon={faCalendarCheck} />
                </div>

                <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: '14px', color: '#1e293b', marginBottom: '4px' }}>
                        {activeReminder.title}
                    </div>
                    <div style={{ fontSize: '12px', color: '#64748b', lineHeight: '1.4', marginBottom: '8px' }}>
                        {activeReminder.content}
                    </div>
                    <button 
                        onClick={() => {
                            if (activeReminder.link) navigate(activeReminder.link);
                            setActiveReminder(null);
                        }}
                        style={{
                            background: '#3b82f6',
                            color: '#fff',
                            border: 'none',
                            padding: '6px 12px',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: 700,
                            cursor: 'pointer'
                        }}
                    >
                        View Lead
                    </button>
                </div>

                <button 
                    onClick={() => setActiveReminder(null)}
                    style={{
                        position: 'absolute',
                        top: '12px',
                        right: '12px',
                        background: 'transparent',
                        border: 'none',
                        color: '#94a3b8',
                        cursor: 'pointer',
                        fontSize: '14px'
                    }}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            
            <style>{`
                @keyframes slideInRight {
                    from { transform: translateX(100%); opacity: 0; }
                    to { transform: translateX(0); opacity: 1; }
                }
                @keyframes progressShrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }
            `}</style>
        </div>
      )}
      {/* 3. Celebration Modal */}
      {celebration && (
        <div className="celebration-overlay" style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.8)',
          backdropFilter: 'blur(8px)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          animation: 'fadeIn 0.5s ease'
        }}>
          <div className="celebration-card" style={{
            background: '#fff',
            maxWidth: '500px',
            width: '100%',
            borderRadius: '32px',
            padding: '40px',
            textAlign: 'center',
            position: 'relative',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            animation: 'popIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)'
          }}>
            <div style={{
              fontSize: '80px',
              marginBottom: '20px',
              animation: 'bounce 2s infinite'
            }}>
              {celebration.icon}
            </div>
            <h1 style={{
              fontSize: '32px',
              fontWeight: 800,
              color: '#1e293b',
              marginBottom: '12px',
              letterSpacing: '-0.02em'
            }}>
              {celebration.title}
            </h1>
            <p style={{
              fontSize: '18px',
              color: '#64748b',
              lineHeight: '1.6',
              marginBottom: '32px'
            }}>
              {celebration.subtitle}
            </p>
            <button 
              onClick={() => setCelebration(null)}
              style={{
                width: '100%',
                padding: '16px',
                borderRadius: '16px',
                border: 'none',
                background: 'linear-gradient(135deg, #799F0C, #a8cc10)',
                color: '#fff',
                fontSize: '16px',
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'transform 0.2s',
                boxShadow: '0 10px 15px -3px rgba(121, 159, 12, 0.3)'
              }}
              onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
            >
              Thank You! ❤️
            </button>
            
            {/* Confetti particles - simulated with CSS */}
            {[...Array(12)].map((_, i) => (
              <div key={i} className="confetti" style={{
                position: 'absolute',
                width: '10px',
                height: '10px',
                background: ['#f87171', '#fbbf24', '#34d399', '#60a5fa', '#a78bfa'][i % 5],
                top: '-20px',
                left: `${Math.random() * 100}%`,
                borderRadius: i % 2 === 0 ? '50%' : '2px',
                animation: `fall ${2 + Math.random() * 3}s linear infinite`,
                animationDelay: `${Math.random() * 2}s`
              }} />
            ))}
          </div>

          <style>{`
            @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
            @keyframes popIn { 
              from { transform: scale(0.8); opacity: 0; } 
              to { transform: scale(1); opacity: 1; } 
            }
            @keyframes bounce {
              0%, 100% { transform: translateY(0); }
              50% { transform: translateY(-15px); }
            }
            @keyframes fall {
              to { transform: translateY(100vh) rotate(360deg); }
            }
          `}</style>
        </div>
      )}
    </>
  );
}
